import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DEFAULT_CONFIG, TimepickerConfig } from '../timepicker/timepicker.config';

@Component({
  selector: 'tp-scroll-column',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './scroll-column.component.html',
  styleUrls: ['./scroll-column.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollColumnComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() items: string[] = [];
  @Input() selectedIndex = 0;
  @Input() config: Required<TimepickerConfig> = { ...DEFAULT_CONFIG };
  @Output() selectedIndexChange = new EventEmitter<number>();

  @ViewChild('scrollEl') scrollEl!: ElementRef<HTMLElement>;

  /** Fractional center position — drives live opacity/size during scroll */
  currentCenter = 0;

  private snapTimer: ReturnType<typeof setTimeout> | null = null;
  private wheelHandler!: (e: WheelEvent) => void;
  private wheelThrottled = false;
  private touchStartY = 0;
  private touchStartScrollTop = 0;
  private isTouching = false;

  /** ms between wheel steps */
  private readonly WHEEL_INTERVAL = 50;
  /** Damping on touch drag (0.55 = 55% of finger travel) */
  private readonly TOUCH_DAMPING = 0.55;

  constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.currentCenter = this.selectedIndex;
    this.scrollToIndex(this.selectedIndex, false);

    this.wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (this.wheelThrottled) return;
      this.wheelThrottled = true;
      setTimeout(() => { this.wheelThrottled = false; }, this.WHEEL_INTERVAL);

      const delta = e.deltaY > 0 ? 1 : -1;
      const next = Math.max(0, Math.min(this.selectedIndex + delta, this.items.length - 1));
      if (next !== this.selectedIndex) {
        this.selectedIndex = next;
        this.currentCenter = next;
        this.scrollToIndex(next, true);
        this.selectedIndexChange.emit(next);
        this.cdr.markForCheck();
      }
    };

    this.ngZone.runOutsideAngular(() => {
      this.scrollEl.nativeElement.addEventListener('wheel', this.wheelHandler, { passive: false });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedIndex'] && !changes['selectedIndex'].firstChange) {
      this.currentCenter = this.selectedIndex;
      this.scrollToIndex(this.selectedIndex, true);
    }
    if (changes['items'] && !changes['items'].firstChange) {
      setTimeout(() => {
        this.currentCenter = this.selectedIndex;
        this.scrollToIndex(this.selectedIndex, false);
        this.cdr.markForCheck();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    if (this.scrollEl?.nativeElement) {
      this.scrollEl.nativeElement.removeEventListener('wheel', this.wheelHandler);
    }
    if (this.snapTimer) clearTimeout(this.snapTimer);
  }

  onScroll(): void {
    const el = this.scrollEl.nativeElement;
    this.currentCenter = el.scrollTop / this.config.itemHeight;
    this.cdr.markForCheck();

    if (this.isTouching) return;
    this.scheduleSnap();
  }

  onTouchStart(e: TouchEvent): void {
    this.isTouching = true;
    this.touchStartY = e.touches[0].clientY;
    this.touchStartScrollTop = this.scrollEl.nativeElement.scrollTop;
    if (this.snapTimer) clearTimeout(this.snapTimer);
  }

  onTouchMove(e: TouchEvent): void {
    e.preventDefault();
    const dy = (this.touchStartY - e.touches[0].clientY) * this.TOUCH_DAMPING;
    this.scrollEl.nativeElement.scrollTop = this.touchStartScrollTop + dy;
  }

  onTouchEnd(): void {
    this.isTouching = false;
    this.scheduleSnap(80);
  }

  selectItem(index: number): void {
    this.selectedIndex = index;
    this.currentCenter = index;
    this.scrollToIndex(index, true);
    this.selectedIndexChange.emit(index);
    this.cdr.markForCheck();
  }

  getItemOpacity(index: number): number {
    const { opacityNear, opacityFar, opacityHidden } = this.config;
    const dist = Math.abs(index - this.currentCenter);
    if (dist <= 0.3) return 1;
    if (dist <= 1.3) return opacityNear;
    if (dist <= 2.3) return opacityFar;
    return opacityHidden;
  }

  getItemFontSize(index: number): string {
    const { fontSizeSelected, fontSizeNear, fontSizeFar } = this.config;
    const dist = Math.abs(index - this.currentCenter);
    if (dist <= 0.3) return `${fontSizeSelected}px`;
    if (dist <= 1.3) return `${fontSizeNear}px`;
    return `${fontSizeFar}px`;
  }

  getItemFontWeight(index: number): string {
    const dist = Math.abs(index - Math.round(this.currentCenter));
    return dist === 0 ? '700' : '400';
  }

  get containerHeight(): number {
    return this.config.visibleItems * this.config.itemHeight;
  }

  get paddingSize(): number {
    return Math.floor(this.config.visibleItems / 2) * this.config.itemHeight;
  }

  private scheduleSnap(delay = 120): void {
    if (this.snapTimer) clearTimeout(this.snapTimer);
    this.snapTimer = setTimeout(() => {
      const el = this.scrollEl.nativeElement;
      const index = Math.round(el.scrollTop / this.config.itemHeight);
      const clamped = Math.max(0, Math.min(index, this.items.length - 1));
      this.scrollToIndex(clamped, true);
      this.ngZone.run(() => {
        this.currentCenter = clamped;
        if (clamped !== this.selectedIndex) {
          this.selectedIndex = clamped;
          this.selectedIndexChange.emit(clamped);
        }
        this.cdr.markForCheck();
      });
    }, delay);
  }

  private scrollToIndex(index: number, animate: boolean): void {
    const el = this.scrollEl?.nativeElement;
    if (!el) return;
    el.scrollTo({
      top: index * this.config.itemHeight,
      behavior: animate ? 'smooth' : 'instant',
    });
  }
}
